import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getStoredAdmin, logout } from "../utils/auth";
import {
  Bell,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Menu,
  Search,
  AlertTriangle,
} from "lucide-react";

interface TopbarProps {
  onMenuClick?: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false); // 👈 new
  const dropdownRef = useRef<HTMLDivElement>(null);
  const admin = getStoredAdmin();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogoutConfirm = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      <header className="bg-white border-b border-beige-300 px-4 md:px-6 h-16 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg text-sage-500 hover:text-sage-900 hover:bg-beige-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu size={20} />
          </button>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Notification Bell */}
          {/* <button className="relative p-2 rounded-lg text-sage-500 hover:text-sage-900 hover:bg-beige-100 transition-colors" aria-label="Notifications">
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-warm-500 rounded-full ring-2 ring-white" />
          </button> */}

          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2.5 p-1.5 pr-3 rounded-lg hover:bg-beige-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-xs font-semibold">
                {(admin?.username?.charAt(0) || admin?.name?.charAt(0) || "A").toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-sage-900 leading-tight">
                  {admin?.username || admin?.name?.split(" ")[0] || "Admin"}
                </p>
                <p className="text-[11px] text-sage-500 leading-tight">Administrator</p>
              </div>
              <ChevronDown size={14} className="hidden sm:block text-sage-400" />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-1.5 w-52 bg-white rounded-xl shadow-elevated border border-beige-300 py-1.5 z-30">
                <div className="px-3 py-2 border-b border-beige-200 mb-1">
                  <p className="text-sm font-semibold text-sage-900">{admin?.username || admin?.name || "Admin"}</p>
                  <p className="text-xs text-sage-500 truncate">{admin?.email || "Administrator"}</p>
                </div>
                <div className="border-t border-beige-200 mt-1 pt-1">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);       // 👈 close dropdown
                      setShowLogoutModal(true);        // 👈 open modal
                    }}
                    className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[320px] text-center">

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="text-red-500" size={28} />
              </div>
            </div>

            {/* Text */}
            <h3 className="text-lg font-semibold text-gray-800">
              Confirm Logout
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to log out?
            </p>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="flex-1 bg-red-500 text-white rounded-lg py-2 text-sm hover:bg-red-600 transition-colors"
              >
                Yes, Log out
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Topbar;